import React, { memo } from 'react';
import { Col, Row, Table } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import {
  FormattedMessage,
  FormattedNumber,
  useIntl,
  IntlShape,
} from 'react-intl';
import dayjs from 'dayjs';
import { Tax } from 'CoreApi.Settings_V12';
import {
  GiftcardReport as GiftcardReportType,
  GiftcardReportTableParams,
} from 'utils/apiClient/services/reports/types';

interface Props {
  currency: string;
  taxes: Array<Tax>;
  reportData: Array<GiftcardReportType> | undefined;
  handleTableChange: (t: GiftcardReportTableParams) => void;
  tableParams: GiftcardReportTableParams;
  onReportSelect: (report: GiftcardReportType) => void;
  isDataLoading: boolean;
}

const formatTaxColumn = (intl: IntlShape, taxRate: string) => {
  const output = parseFloat(taxRate);
  return taxRate !== '0.000'
    ? `${intl.formatMessage({
        defaultMessage: 'Single Purpose',
      })} (${output}%)`
    : `${intl.formatMessage({
        defaultMessage: 'Multi Purpose',
      })} (${output}%)`;
};

const GiftcardReportTable: React.FC<Props> = ({
  currency,
  taxes,
  reportData,
  handleTableChange,
  tableParams,
  onReportSelect,
  isDataLoading,
}) => {
  const intl = useIntl();

  return (
    <Row>
      <Col span={24}>
        <Table<GiftcardReportType>
          css={`
            padding-bottom: 15px;
            margin-top: 20px;
            .ant-table-placeholder {
              min-height: calc(100vh - 300px);
            }
          `}
          dataSource={reportData}
          rowKey={({ id }) => id}
          size="middle"
          loading={isDataLoading}
          pagination={tableParams.pagination as TablePaginationConfig}
          onChange={(pagination) => handleTableChange({ pagination })}
          onRow={(r: GiftcardReportType) => ({
            onClick: () => onReportSelect(r),
          })}
          scroll={{ x: true }}
        >
          <Table.Column<GiftcardReportType>
            key="name"
            width="30%"
            title={<FormattedMessage defaultMessage="Name" />}
            dataIndex="name"
          />
          <Table.Column<GiftcardReportType>
            key="redeem_code"
            width="20%"
            title={<FormattedMessage defaultMessage="Coupon code" />}
            dataIndex="redeem_code"
            render={(redeem_code: string) => (
              <span css="color: #54b25c;">{redeem_code}</span>
            )}
          />
          <Table.Column<GiftcardReportType>
            key="tax_type"
            width="15%"
            title={<FormattedMessage defaultMessage="Type" />}
            dataIndex="tax_type"
            render={(record) => {
              const taxRate = taxes.find((t) => t.id === record)?.tax_rate;
              if (taxRate) {
                return <span>{formatTaxColumn(intl, taxRate)}</span>;
              }
              return null;
            }}
          />
          <Table.Column<GiftcardReportType>
            key="created_at"
            width="15%"
            title={<FormattedMessage defaultMessage="Sold" />}
            dataIndex="created_at"
            render={(created_at: string) =>
              dayjs(created_at).format('MM/DD/YYYY')
            }
          />
          <Table.Column<GiftcardReportType>
            key="invoice_number"
            width="10%"
            title={<FormattedMessage defaultMessage="Invoice Nr." />}
            dataIndex="invoice_number"
          />
          <Table.Column<GiftcardReportType>
            key="amount"
            width="10%"
            title={<FormattedMessage defaultMessage="Value" />}
            dataIndex="amount"
            render={(amount: number) => (
              <FormattedNumber
                style="currency" // eslint-disable-line react/style-prop-object
                currency={currency}
                value={amount}
              />
            )}
          />
          <Table.Column<GiftcardReportType>
            key="current_value"
            width="10%"
            title={<FormattedMessage defaultMessage="Remaining" />}
            dataIndex="current_value"
            render={(current_value: number) => (
              <FormattedNumber
                style="currency" // eslint-disable-line react/style-prop-object
                currency={currency}
                value={current_value}
              />
            )}
          />
        </Table>
      </Col>
    </Row>
  );
};

export default memo(GiftcardReportTable);
